import React, { useState } from "react";
import Box from "@mui/material/Box";
import { SideBarContainer } from "../components/Layout/SideBarContainer";
import { DamsLogoLabel } from "../components/Layout/DamsLogoLabel";
import { FooterItem } from "../components/Layout/SideBarItem";
import SideBarList, {
  MenuItem,
  SideBarFooterList,
} from "../components/Layout/SideBarList";
import { Divider } from "@mui/material";
import VerticalGap from "../components/Gap/VerticalGap";
import { DamsColors } from "../components/styles/DamsColors";
import { Outlet, useNavigate } from "react-router-dom";
import { SideBarOutletContainer } from "./OutletContainer";
import InfoModal from "../components/Modals/InfoModal";
import NotificationModal from "../components/Modals/NotificationModal";
import { AuthGuard } from "../common-ui/AuthGuard";
import useAccount from "../hooks/useAccount";
import { SignOut } from "../services/auth/signOut";
import { appConfig } from "../config/config";

import { systemUser, type SystemUser } from "../config/constants/system-users";
import { navIcons, navPages } from "../config/constants/nav-pages";
import { useAppSelector } from "../store/hooks";

const SidebarLayout: React.FC = () => {
  const [menuList, setMenuList] = useState<MenuItem[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<string>("");
  const [selectedFooterMenu, setSelectedFooterMenu] = useState<string>("");

  const currentUser = useAppSelector((data) => data.authVerify);

  // welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);
  const [welcomeInfoStage, setWelcomeInfoStage] = useState(1);

  // User Status modal
  const [showUserStatus, setShowUserStatus] = useState(false);

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!currentUser.role) {
      return;
    }
    const menuOption: SystemUser = currentUser.role;
    const menu = MainLayoutMenuData.get(menuOption);
    if (menu) {
      setMenuList(menu);
    }
  }, [currentUser.role]);

  function onMenuClick(menu: MenuItem, index: string | number) {
    setSelectedFooterMenu(``);
    setSelectedMenu(`${index}`);

    navigate(menu.path!);
  }

  function onFooterMenuClick(menu: MenuItem, index: string | number) {
    setSelectedMenu(``);
    setSelectedFooterMenu(`${index}`);
    if (menu.title === "Support") {
      window.open(menu.path, "_blank", "noopener,noreferrer");
    } else {
      navigate(menu.path!);
    }
  }

  function onLogoClick() {}

  function onLogoutclick() {
    SignOut();
    navigate("/auth");
  }

  function onNotificationClick() {}

  // welcome modal fxns
  function onSkipPress() {
    setShowWelcomeModal(false);
    setShowUserStatus(true);
  }

  function onNextPress() {
    if (welcomeInfoStage < 4) {
      setWelcomeInfoStage(welcomeInfoStage + 1);
    } else {
      // TO DO : close modal
      setShowWelcomeModal(false);
      setShowUserStatus(true);
    }
  }

  // User Status fxns
  function onUserStatusCancelPress() {
    setShowUserStatus(false);
  }

  function onGetStartedPress() {
    setShowUserStatus(false);
    navigate("/users");
  }

  const { showTips, profile } = useAccount();
  if (menuList.length === 0) {
    return <>loading</>;
  }
  return (
    <Box flexGrow={1}>
      <SideBarContainer>
        <DamsLogoLabel title="DAMMS" />

        <Box m={"24px"} />

        <SideBarList
          ItemList={menuList}
          onItemClick={onMenuClick}
          selectedItem={selectedMenu}
          onLogoutClick={onLogoClick}
        />

        <Box sx={{ flexGrow: 1 }} />

        <SideBarFooterList
          ItemList={SideBarLayoutFooterData}
          onItemClick={onFooterMenuClick}
          selectedItem={selectedFooterMenu}
          onNotificationClick={onNotificationClick}
        />

        <VerticalGap height={24} />

        <Divider variant="middle" sx={styles.footerDivider} />

        <VerticalGap height={24} />

        <FooterItem
          logoutBtn={onLogoutclick}
          userName={"Welcome, " + profile.first_name || "Welcome"}
          userTitle={profile.user_category}
        />
      </SideBarContainer>

      {/* TO DO : Add Outlet Container  */}
      <SideBarOutletContainer>
        <Outlet />
      </SideBarOutletContainer>

      {showTips && (
        <InfoModal
          title={welcome_modal_info.title}
          subtitle={welcome_modal_info.subtitle}
          infoStage={welcomeInfoStage}
          onSkipPress={() => onSkipPress()}
          onNextPress={() => onNextPress()}
          showModal={showWelcomeModal}
        />
      )}
      <NotificationModal
        title={user_status_notification["user-1"].title}
        subtitle={user_status_notification["user-1"].subtitle}
        showModal={showUserStatus}
        onCancelPress={() => onUserStatusCancelPress()}
        onGetStartedPress={() => onGetStartedPress()}
      />
    </Box>
  );
};

const styles = {
  footerDivider: {
    color: DamsColors.Border.FooterDivider,
  },
};

const welcome_modal_info = {
  title: "Welcome to DAMMS",
  subtitle:
    "We are glad to have you. Here are some quick tips to get you around your dashboard",
};

const user_status_notification = {
  "user-1": {
    title: "You've been made a National Manager",
    subtitle:
      "You have been added to the DAMMS Admin Panel.Now you can add new users",
  },
  "user-2": {
    title: "Notifications",
    subtitle: "You have 2 new notifications",
  },
  "user-3": {
    title: "Notifications",
    subtitle: "You have 1 new notifications",
  },
  "user-4": {
    title: "Notifications",
    subtitle: "You have 4 new notifications",
  },
  "user-5": {
    title: "Notifications",
    subtitle: "You have 0 new notifications",
  },
};

const MainLayoutMenuData = new Map<SystemUser, MenuItem[]>();

MainLayoutMenuData.set(systemUser.SUPER_ADMIN, [
  navPages.DASHBOARD.HOME,
  {
    title: "Asset Management",
    children: [
      navPages.ASSET_REGISTRY.ADD_ASSET_REGISTRY_PAGE,
      navPages.ASSET_REGISTRY.ASSET_REGISTRY_HISTORY_PAGE,
    ],
    icon: navIcons.assets,
  },
  {
    title: "Asset Distribution",
    children: [
      navPages.ASSET_DISTRIBUTION.ADD_ASSET_DISTRIBUTION_PAGE,
      navPages.ASSET_DISTRIBUTION.INBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.OUTBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.REJECTED_INBOUND_ASSET_DISTRIBUTIONS_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Dispatch",
    children: [
      navPages.ASSET_DISPATCH.ADD_ASSET_DISPATCH_PAGE,
      navPages.ASSET_DISPATCH.ASSET_DISPATCH_HISTORY_PAGE,
    ],
    icon: navIcons.default,
  },

  {
    title: "Asset Assignment",
    children: [
      navPages.ASSET_ASSIGNMENT.NEW_ASSET_ASSIGNMENT_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_IN_MY_POSSESSION_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_INBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_OUTBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.REJECTED_INBOUND_ASSET_ASSIGNEMENT_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Report",
    children: [
      navPages.ASSET_REPORT.ASSET_REPORT_FORM,
      navPages.ASSET_REPORT.ASSET_REPORT_HISTORY,
    ],
    icon: navIcons.default,
  },
  {
    title: "Maintenance Schedule",
    children: [
      navPages.ASSET_MAINTENANCE.ADD_ASSET_MAINTENANCE_PAGE,
      navPages.ASSET_MAINTENANCE.ASSET_MAINTENANCE_HISTORY_PAGE,
    ],
    icon: navIcons.maintenance,
  },

  {
    title: "User Management",
    children: [
      navPages.USER_DIRECTORY.USERS_PAGE,
      navPages.USER_DIRECTORY.USERS_ROLES_PAGE,
      navPages.USER_DIRECTORY.USER_CHANGE_PASSWORD_PAGE,
    ],
    icon: navIcons.default,
  },

  {
    title: "Settings",
    path: "/settings",
    children: [
      navPages.SETTINGS.MANAGE_DIVISIONS_PAGE,
      navPages.SETTINGS.MANAGE_PROGRAMMES_PAGE,
      navPages.SETTINGS.MANAGE_FACILITIES_PAGE,
      navPages.SETTINGS.MANAGE_REGIONS_PAGE,
      navPages.SETTINGS.MANAGE_DISTRICTS_PAGE,
      navPages.SETTINGS.MANAGE_DONORS_PAGE,
      navPages.SETTINGS.MANAGE_BATCHES_PAGE,
    ],
    icon: navIcons.settings,
  },
  {
    title: "More",
    children: [
      navPages.SETTINGS.CATEGORIES.MANAGE_ASSET_CATEGORIES_PAGE,
      navPages.SETTINGS.CATEGORIES.MANAGE_ASSET_TAGS_PAGE,
      navPages.SETTINGS.CATEGORIES.MANAGE_USER_CATEGORIES_PAGE,
    ],
    icon: navIcons.more,
  },
]);

MainLayoutMenuData.set(systemUser.IT_MANAGER, [
  navPages.DASHBOARD.HOME,
  {
    title: "Asset Management",
    children: [
      navPages.ASSET_REGISTRY.ADD_ASSET_REGISTRY_PAGE,
      navPages.ASSET_REGISTRY.ASSET_REGISTRY_HISTORY_PAGE,
    ],
    icon: navIcons.assets,
  },
  {
    title: "Asset Assignment",
    children: [
      navPages.ASSET_ASSIGNMENT.ASSET_IN_MY_POSSESSION_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_INBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.REJECTED_INBOUND_ASSET_ASSIGNEMENT_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Report",
    children: [
      navPages.ASSET_REPORT.ASSET_REPORT_FORM,
      navPages.ASSET_REPORT.ASSET_REPORT_HISTORY,
    ],
    icon: navIcons.default,
  },
  {
    title: "Maintenance Schedule",
    children: [
      navPages.ASSET_MAINTENANCE.ADD_ASSET_MAINTENANCE_PAGE,
      navPages.ASSET_MAINTENANCE.ASSET_MAINTENANCE_HISTORY_PAGE,
    ],
    icon: navIcons.maintenance,
  },

  {
    title: "User Management",
    children: [
      navPages.USER_DIRECTORY.USERS_PAGE,
      navPages.USER_DIRECTORY.USER_CHANGE_PASSWORD_PAGE,
    ],
    icon: navIcons.users,
  },
  {
    title: "Settings",
    children: [
      navPages.SETTINGS.MANAGE_DONORS_PAGE,
      navPages.SETTINGS.MANAGE_BATCHES_PAGE,
    ],
    icon: navIcons.settings,
  },
  {
    title: "More",
    children: [
      navPages.SETTINGS.CATEGORIES.MANAGE_ASSET_CATEGORIES_PAGE,
      navPages.SETTINGS.CATEGORIES.MANAGE_USER_CATEGORIES_PAGE,
    ],
    icon: navIcons.more,
  },
]);
MainLayoutMenuData.set(systemUser.IT_ADMIN, [
  navPages.DASHBOARD.HOME,
  {
    title: "Asset Management",
    children: [
      navPages.ASSET_REGISTRY.ADD_ASSET_REGISTRY_PAGE,
      navPages.ASSET_REGISTRY.ASSET_REGISTRY_HISTORY_PAGE,
    ],
    icon: navIcons.assets,
  },
  {
    title: "Asset Assignment",
    children: [
      navPages.ASSET_ASSIGNMENT.ASSET_IN_MY_POSSESSION_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_INBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.REJECTED_INBOUND_ASSET_ASSIGNEMENT_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Report",
    children: [
      navPages.ASSET_REPORT.ASSET_REPORT_FORM,
      navPages.ASSET_REPORT.ASSET_REPORT_HISTORY,
    ],
    icon: navIcons.default,
  },
  {
    title: "Maintenance Schedule",
    children: [
      navPages.ASSET_MAINTENANCE.ADD_ASSET_MAINTENANCE_PAGE,
      navPages.ASSET_MAINTENANCE.ASSET_MAINTENANCE_HISTORY_PAGE,
    ],
    icon: navIcons.maintenance,
  },

  {
    title: "User Management",
    children: [
      navPages.USER_DIRECTORY.USERS_PAGE,
      navPages.USER_DIRECTORY.USER_CHANGE_PASSWORD_PAGE,
    ],
    icon: navIcons.users,
  },
  {
    title: "Settings",
    children: [
      navPages.SETTINGS.MANAGE_DONORS_PAGE,
      navPages.SETTINGS.MANAGE_BATCHES_PAGE,
    ],
    icon: navIcons.settings,
  },
  {
    title: "More",
    children: [
      navPages.SETTINGS.CATEGORIES.MANAGE_ASSET_CATEGORIES_PAGE,
      navPages.SETTINGS.CATEGORIES.MANAGE_USER_CATEGORIES_PAGE,
    ],
    icon: navIcons.more,
  },
]);

MainLayoutMenuData.set(systemUser.STORE_MANAGER, [
  navPages.DASHBOARD.HOME,
  navPages.ASSET_REGISTRY.ASSET_REGISTRY_HISTORY_PAGE,
  {
    title: "User Management",
    children: [navPages.USER_DIRECTORY.USER_CHANGE_PASSWORD_PAGE],
    icon: navIcons.users,
  },
  {
    title: "Asset Distribution",
    children: [
      navPages.ASSET_DISTRIBUTION.ADD_ASSET_DISTRIBUTION_PAGE,
      navPages.ASSET_DISTRIBUTION.INBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.OUTBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.REJECTED_INBOUND_ASSET_DISTRIBUTIONS_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Dispatch",
    children: [
      navPages.ASSET_DISPATCH.ADD_ASSET_DISPATCH_PAGE,
      navPages.ASSET_DISPATCH.ASSET_DISPATCH_HISTORY_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Assignment",
    children: [
      navPages.ASSET_ASSIGNMENT.NEW_ASSET_ASSIGNMENT_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_IN_MY_POSSESSION_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_INBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_OUTBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.REJECTED_INBOUND_ASSET_ASSIGNEMENT_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Report",
    children: [
      navPages.ASSET_REPORT.ASSET_REPORT_FORM,
      navPages.ASSET_REPORT.ASSET_REPORT_HISTORY,
    ],
    icon: navIcons.default,
  },
  navPages.SETTINGS.MANAGE_BATCHES_PAGE,
]);
MainLayoutMenuData.set(systemUser.STORE_ADMIN, [
  navPages.DASHBOARD.HOME,
  navPages.ASSET_REGISTRY.ASSET_REGISTRY_HISTORY_PAGE,
  {
    title: "User Management",
    children: [navPages.USER_DIRECTORY.USER_CHANGE_PASSWORD_PAGE],
    icon: navIcons.users,
  },
  {
    title: "Asset Distribution",
    children: [
      navPages.ASSET_DISTRIBUTION.ADD_ASSET_DISTRIBUTION_PAGE,
      navPages.ASSET_DISTRIBUTION.INBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.OUTBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.REJECTED_INBOUND_ASSET_DISTRIBUTIONS_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Dispatch",
    children: [
      navPages.ASSET_DISPATCH.ADD_ASSET_DISPATCH_PAGE,
      navPages.ASSET_DISPATCH.ASSET_DISPATCH_HISTORY_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Assignment",
    children: [
      navPages.ASSET_ASSIGNMENT.NEW_ASSET_ASSIGNMENT_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_IN_MY_POSSESSION_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_INBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_OUTBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.REJECTED_INBOUND_ASSET_ASSIGNEMENT_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Report",
    children: [
      navPages.ASSET_REPORT.ASSET_REPORT_FORM,
      navPages.ASSET_REPORT.ASSET_REPORT_HISTORY,
    ],
    icon: navIcons.default,
  },
  navPages.SETTINGS.MANAGE_BATCHES_PAGE,
]);

MainLayoutMenuData.set(systemUser.STAFF, [
  {
    title: "Asset Assignment",
    children: [
      navPages.ASSET_ASSIGNMENT.ASSET_IN_MY_POSSESSION_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_INBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.REJECTED_INBOUND_ASSET_ASSIGNEMENT_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Report",
    children: [
      navPages.ASSET_REPORT.ASSET_REPORT_FORM,
      navPages.ASSET_REPORT.ASSET_REPORT_HISTORY,
    ],
    icon: navIcons.default,
  },
  {
    title: "User Management",
    children: [navPages.USER_DIRECTORY.USER_CHANGE_PASSWORD_PAGE],
    icon: navIcons.users,
  },
]);

MainLayoutMenuData.set(systemUser.DIRECTOR, [
  navPages.DASHBOARD.HOME,
  {
    title: "Asset Management",
    children: [
      navPages.ASSET_REGISTRY.ADD_ASSET_REGISTRY_PAGE,
      navPages.ASSET_REGISTRY.ASSET_REGISTRY_HISTORY_PAGE,
    ],
    icon: navIcons.assets,
  },
  {
    title: "Asset Distribution",
    children: [
      navPages.ASSET_DISTRIBUTION.ADD_ASSET_DISTRIBUTION_PAGE,
      navPages.ASSET_DISTRIBUTION.INBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.OUTBOUND_ASSET_DISTRIBUTIONS_PAGE,
      navPages.ASSET_DISTRIBUTION.REJECTED_INBOUND_ASSET_DISTRIBUTIONS_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Dispatch",
    children: [
      navPages.ASSET_DISPATCH.ADD_ASSET_DISPATCH_PAGE,
      navPages.ASSET_DISPATCH.ASSET_DISPATCH_HISTORY_PAGE,
    ],
    icon: navIcons.default,
  },

  {
    title: "Asset Assignment",
    children: [
      navPages.ASSET_ASSIGNMENT.NEW_ASSET_ASSIGNMENT_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_IN_MY_POSSESSION_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_INBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.ASSET_OUTBOUND_ASSIGNMENT_HISTORY_PAGE,
      navPages.ASSET_ASSIGNMENT.REJECTED_INBOUND_ASSET_ASSIGNEMENT_PAGE,
    ],
    icon: navIcons.default,
  },
  {
    title: "Asset Report",
    children: [
      navPages.ASSET_REPORT.ASSET_REPORT_FORM,
      navPages.ASSET_REPORT.ASSET_REPORT_HISTORY,
    ],
    icon: navIcons.default,
  },
  {
    title: "Maintenance Schedule",
    children: [
      navPages.ASSET_MAINTENANCE.ADD_ASSET_MAINTENANCE_PAGE,
      navPages.ASSET_MAINTENANCE.ASSET_MAINTENANCE_HISTORY_PAGE,
    ],
    icon: navIcons.maintenance,
  },

  {
    title: "User Management",
    children: [
      navPages.USER_DIRECTORY.USERS_PAGE,
      navPages.USER_DIRECTORY.USERS_ROLES_PAGE,
      navPages.USER_DIRECTORY.USER_CHANGE_PASSWORD_PAGE,
    ],
    icon: navIcons.default,
  },

  {
    title: "Settings",
    path: "/settings",
    children: [
      navPages.SETTINGS.MANAGE_DIVISIONS_PAGE,
      navPages.SETTINGS.MANAGE_PROGRAMMES_PAGE,
      navPages.SETTINGS.MANAGE_FACILITIES_PAGE,
      navPages.SETTINGS.MANAGE_REGIONS_PAGE,
      navPages.SETTINGS.MANAGE_DISTRICTS_PAGE,
      navPages.SETTINGS.MANAGE_DONORS_PAGE,
      navPages.SETTINGS.MANAGE_BATCHES_PAGE,
    ],
    icon: navIcons.settings,
  },
  {
    title: "More",
    children: [
      navPages.SETTINGS.CATEGORIES.MANAGE_ASSET_CATEGORIES_PAGE,
      navPages.SETTINGS.CATEGORIES.MANAGE_USER_CATEGORIES_PAGE,
    ],
    icon: navIcons.more,
  },
]);

const SideBarLayoutFooterData = [
  { title: "Support", path: appConfig.ghostUrl, icon: navIcons.support },
];

const SidebarWithAuth = () => (
  <AuthGuard>
    <SidebarLayout />
  </AuthGuard>
);

export default SidebarWithAuth;
